/* eslint-disable react/prop-types */
import React from "react";
import {
  Box,
  Button,
  Container,
  Flex,
  Heading,
  HStack,
  Modal,
  ModalBody,
  ModalContent,
  ModalOverlay,
  Spinner,
  Text,
  Textarea,
  VStack,
  useToast,
} from "@chakra-ui/react";
import { useMutation, useQuery } from "@apollo/client";
import { useNavigate, useSearchParams } from "react-router-dom";

import WithAppLayout from "../../../components/WithAppLayout";
import MoblyzeApolloClient from "../../../services/MoblyzeApolloClient";
import {
  CREATE_JOB_MUTATION,
  GENERATE_JOB_LISTING_DRAFT_MUTATION,
  JOB_CREATE_PAGE_QUERY,
  JOB_POSTING_SOURCE_CONTEXT_QUERY,
  UPLOAD_JOB_COVER_IMAGE_MUTATION,
} from "../../../graphql/jobs_admin";
import {
  ADD_JOB_CLASSIC_PATH,
  SCREENING_CRITERIA_PATH,
} from "../../../routes";
import { FLAGS, useBoolFlag } from "../../../services/featureFlags";
import { applyContactFieldsForAgencyType } from "../../../helpers/jobContactFields";
import { normalizeBulletDescription } from "../../../helpers/jobListingDescription";
import { ownerFromUser, ownerUserIdVariables } from "../../../helpers/jobOwner";
import {
  draftFromApi,
  MIN_CONTEXT_LENGTH,
  missingRequiredFields,
} from "../../../helpers/jobCreateFlowHelpers";
import {
  COVER_IMAGES,
  PreviewCard,
  REQUIRED_BEFORE_PUBLISH_GLOW_PROPS,
  urlToFile,
} from "../../../components/JobPreviewCard";
import {
  ClientOrganizationSelect,
  EditModal,
} from "../../../components/JobPreviewEditModal";

const PUBLISH_STATUS_MESSAGES = [
  "Publishing your job listing…",
  "Matching candidate roles to this posting…",
  "Extracting required skills…",
  "Identifying relevant certifications…",
  "Saving your job…",
  "Uploading cover image…",
];

const PUBLISH_STATUS_INTERVAL_MS = 4500;

function PublishLoadingOverlay({ isOpen, message }) {
  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {}}
      closeOnOverlayClick={false}
      closeOnEsc={false}
      isCentered
      variant="darkMode"
    >
      <ModalOverlay bg="blackAlpha.800" />
      <ModalContent bg="gray.800" mx={4}>
        <ModalBody py={10} px={8}>
          <VStack spacing={6}>
            <Spinner
              size="xl"
              thickness="4px"
              speed="0.65s"
              color="blue.300"
              emptyColor="whiteAlpha.300"
            />
            <VStack spacing={2}>
              <Heading size="md" textAlign="center">
                Publishing job
              </Heading>
              <Text
                fontSize="md"
                color="gray.300"
                textAlign="center"
                minH="48px"
                transition="opacity 0.2s"
              >
                {message}
              </Text>
              <Text fontSize="sm" color="gray.500" textAlign="center">
                This usually takes 20–30 seconds while we analyse the listing.
              </Text>
            </VStack>
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}

function JobCreateFlowPage({ currentUser }) {
  const bullhornPrefillEnabled = useBoolFlag(FLAGS.WEB_JOB_BULLHORN_PREFILL);
  const [searchParams] = useSearchParams();
  const bullhornId = searchParams.get("bullhornId") || "";
  const navigate = useNavigate();
  const toast = useToast();

  const [phase, setPhase] = React.useState("input");
  const [jobContext, setJobContext] = React.useState("");
  const [draft, setDraft] = React.useState(null);
  const [generationRunId, setGenerationRunId] = React.useState(null);
  const [editField, setEditField] = React.useState(null);
  const [prefillError, setPrefillError] = React.useState(null);
  const [publishing, setPublishing] = React.useState(false);
  const [publishStatusIndex, setPublishStatusIndex] = React.useState(0);

  React.useEffect(() => {
    if (!publishing) {
      setPublishStatusIndex(0);
      return undefined;
    }
    const interval = setInterval(() => {
      setPublishStatusIndex((i) => (i + 1) % PUBLISH_STATUS_MESSAGES.length);
    }, PUBLISH_STATUS_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [publishing]);

  const { data: pageData } = useQuery(JOB_CREATE_PAGE_QUERY);
  const clientOrgs = pageData?.getClientOrganizations || [];
  const vessels = pageData?.getVessels || [];
  const workLocationNameById = React.useMemo(() => {
    const map = {};
    (pageData?.candidateWorkLocations || []).forEach((loc) => {
      map[loc.id] = loc.name;
    });
    return map;
  }, [pageData]);
  const workLocationOptions = React.useMemo(
    () =>
      (pageData?.candidateWorkLocations || []).map((loc) => ({
        value: loc.id,
        label: loc.name,
      })),
    [pageData]
  );

  const agencyType = currentUser?.agency?.type ?? "";
  const shouldFetchBullhornPrefill = bullhornPrefillEnabled && Boolean(bullhornId);

  const { loading: prefillLoading } = useQuery(JOB_POSTING_SOURCE_CONTEXT_QUERY, {
    variables: { bullhornId },
    skip: !shouldFetchBullhornPrefill,
    fetchPolicy: "network-only",
    onCompleted: (data) => {
      const ctx = data?.jobPostingSourceContext;
      if (ctx?.prefillText) {
        setJobContext(ctx.prefillText);
      }
      const hintId = ctx?.clientOrganizationHint?.clientOrganizationId;
      if (hintId) {
        setDraft((d) =>
          d?.clientOrganizationId ? d : { ...(d || {}), clientOrganizationId: hintId }
        );
      }
      setPrefillError(null);
    },
    onError: (err) => {
      setPrefillError(err.message);
    },
  });

  const [generateDraft, { loading: generating }] = useMutation(
    GENERATE_JOB_LISTING_DRAFT_MUTATION
  );

  const handleGenerate = async () => {
    const trimmed = jobContext.trim();
    if (!draft?.clientOrganizationId) {
      toast({
        title: "Select a department",
        description: "Choose a department before generating a draft listing.",
        status: "warning",
      });
      return;
    }
    if (trimmed.length < MIN_CONTEXT_LENGTH) {
      toast({
        title: "Add more context",
        description: `Please enter at least ${MIN_CONTEXT_LENGTH} characters.`,
        status: "warning",
      });
      return;
    }
    try {
      const { data } = await generateDraft({
        variables: {
          input: {
            jobContext: trimmed,
            bullhornId: bullhornId || null,
            clientOrganizationId: draft.clientOrganizationId,
          },
        },
      });
      const payload = data?.generateJobListingDraft;
      setGenerationRunId(payload.generationRunId);
      setDraft(
        applyContactFieldsForAgencyType(
          {
            ...draftFromApi(payload.draft),
            // The creator owns the job unless they pick someone else; a pick
            // survives going back to the context and regenerating.
            owner: draft.owner || ownerFromUser(currentUser),
          },
          agencyType
        )
      );
      setPhase("review");
    } catch (e) {
      toast({ title: "Generation failed", description: e.message, status: "error" });
    }
  };

  const handlePublish = async () => {
    const missing = missingRequiredFields(draft);
    if (missing.length) {
      toast({
        title: "Complete required fields",
        description: `Missing: ${missing.join(", ")}`,
        status: "warning",
      });
      return;
    }
    try {
      setPublishing(true);
      const publishDraft = applyContactFieldsForAgencyType(
        {
          ...draft,
          requirementsDescription: draft.requirementsDescription
            ? normalizeBulletDescription(draft.requirementsDescription)
            : draft.requirementsDescription,
          responsibilitiesDescription: draft.responsibilitiesDescription
            ? normalizeBulletDescription(draft.responsibilitiesDescription)
            : draft.responsibilitiesDescription,
        },
        agencyType
      );
      const coverFile = await urlToFile(COVER_IMAGES[publishDraft.coverImageIndex] || COVER_IMAGES[0]);
      const variables = {
        bullhornId: bullhornId || publishDraft.bullhornId || null,
        category: publishDraft.category || null,
        employmentType: publishDraft.employmentType,
        numOpenings: publishDraft.numOpenings,
        clientOrganizationId: publishDraft.clientOrganizationId,
        employerContactName: publishDraft.employerContactName,
        employerContactEmail: publishDraft.employerContactEmail,
        employerContactPhone: publishDraft.employerContactPhone,
        agencyContactName: publishDraft.agencyContactName,
        agencyContactEmail: publishDraft.agencyContactEmail,
        agencyContactPhone: publishDraft.agencyContactPhone,
        location: publishDraft.location,
        duration: publishDraft.duration,
        startDate: publishDraft.isAsap ? null : publishDraft.startDate,
        isAsap: publishDraft.isAsap || false,
        requirementsDescription: publishDraft.requirementsDescription,
        responsibilitiesDescription: publishDraft.responsibilitiesDescription,
        otherDescription: publishDraft.otherDescription,
        vesselId: publishDraft.vesselId,
        candidateWorkLocationIds: publishDraft.candidateWorkLocationIds || [],
        payRateValue: publishDraft.payRateValue,
        payFrequency: publishDraft.payFrequency,
        payCurrency: publishDraft.payCurrency,
        ...ownerUserIdVariables(publishDraft.owner, currentUser?.id),
        generationRunId,
      };

      const r = await MoblyzeApolloClient.mutate({
        mutation: CREATE_JOB_MUTATION,
        variables,
      });
      const jobId = r.data.createJob.id;

      try {
        await MoblyzeApolloClient.mutate({
          mutation: UPLOAD_JOB_COVER_IMAGE_MUTATION,
          variables: { moblyzeJobId: jobId, imageFile: coverFile },
        });
        toast({ title: "Job published", status: "success" });
      } catch (coverErr) {
        toast({
          title: "Job published without cover image",
          description:
            "The listing was created, but uploading the cover image failed. You can add it from the job editor. " +
            coverErr.message,
          status: "warning",
          duration: 12000,
          isClosable: true,
        });
      }
      navigate(`${SCREENING_CRITERIA_PATH.replace(":id", jobId)}?new=true`);
    } catch (e) {
      toast({ title: "Publish failed", description: e.message, status: "error" });
    } finally {
      setPublishing(false);
    }
  };

  const missing = missingRequiredFields(draft);
  const canPublish = phase === "review" && missing.length === 0 && !publishing;
  const canGenerate =
    jobContext.trim().length >= MIN_CONTEXT_LENGTH && Boolean(draft?.clientOrganizationId);

  return (
    <>
      {phase === "input" && (
        <Container maxW="container.md" py={10}>
          <VStack spacing={6} align="stretch">
            <Box>
              <Heading size="lg" mb={2}>New job</Heading>
              <Text color="gray.300">
                Just a rough outline is fine. We'll fill in the blanks. You can review before publishing.
              </Text>
            </Box>
            {shouldFetchBullhornPrefill && prefillLoading && (
              <HStack><Spinner size="sm" /><Text fontSize="sm">Loading Bullhorn job…</Text></HStack>
            )}
            {shouldFetchBullhornPrefill && prefillError && (
              <Text fontSize="sm" color="orange.300">
                Bullhorn pre-fill failed ({prefillError}). You can paste context manually.
              </Text>
            )}
            <Box>
              <Text fontSize="sm" mb={2}>Department <Text as="span" color="red.300">*</Text></Text>
              <ClientOrganizationSelect
                clientOrgs={clientOrgs}
                value={draft?.clientOrganizationId}
                onChange={(clientOrganizationId) =>
                  setDraft((d) => ({ ...(d || {}), clientOrganizationId }))
                }
              />
            </Box>
            <Textarea
              placeholder="e.g. We need a senior subsea engineer for a 6-month North Sea contract starting in August, based out of Aberdeen, around £650/day…"
              minH="220px"
              value={jobContext}
              onChange={(e) => setJobContext(e.target.value)}
              isDisabled={generating}
              variant="outlineDark"
            />
            <Flex justify="space-between" align="center">
              <Button variant="ghost" size="sm" onClick={() => navigate(ADD_JOB_CLASSIC_PATH)}>
                Use classic wizard
              </Button>
              <Button
                colorScheme="blue"
                size="lg"
                onClick={handleGenerate}
                isLoading={generating}
                loadingText="Drafting listing"
                isDisabled={!canGenerate}
              >
                Generate job listing
              </Button>
            </Flex>
          </VStack>
        </Container>
      )}

      {phase === "review" && draft && (
        <Container maxW="container.lg" py={6}>
          <Flex justify="space-between" align="center" mb={4} gap={4} wrap="wrap">
            <Box>
              <Heading size="lg">Review your listing</Heading>
              <Button
                variant="link"
                size="sm"
                color="blue.300"
                mt={1}
                onClick={() => setPhase("input")}
              >
                Back to context
              </Button>
            </Box>
            <Button
              colorScheme="green"
              size="lg"
              onClick={handlePublish}
              isDisabled={!canPublish}
            >
              Publish
            </Button>
          </Flex>
          <Box maxW="720px">
            {missing.length > 0 && (
              <Box
                mb={4}
                p={3}
                bg="orange.900"
                {...REQUIRED_BEFORE_PUBLISH_GLOW_PROPS}
              >
                <Text fontSize="sm" color="orange.100" fontWeight="semibold">
                  Required before publish: {missing.join(", ")}
                </Text>
              </Box>
            )}
            <PreviewCard
              draft={draft}
              vessels={vessels}
              clientOrgs={clientOrgs}
              workLocationNameById={workLocationNameById}
              agencyType={agencyType}
              onEdit={setEditField}
            />
          </Box>
        </Container>
      )}

      <EditModal
        field={editField}
        draft={draft}
        agencyType={agencyType}
        clientOrgs={clientOrgs}
        vessels={vessels}
        workLocationOptions={workLocationOptions}
        onClose={() => setEditField(null)}
        onSave={(patch) => {
          const next = { ...patch };
          if (typeof next.requirementsDescription === "string") {
            next.requirementsDescription = normalizeBulletDescription(next.requirementsDescription);
          }
          if (typeof next.responsibilitiesDescription === "string") {
            next.responsibilitiesDescription = normalizeBulletDescription(next.responsibilitiesDescription);
          }
          setDraft((d) => ({ ...d, ...next }));
          setEditField(null);
        }}
      />

      <PublishLoadingOverlay
        isOpen={publishing}
        message={PUBLISH_STATUS_MESSAGES[publishStatusIndex]}
      />
    </>
  );
}

export { JobCreateFlowPage };
export default WithAppLayout(JobCreateFlowPage);
